import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { currency } from "@/components/Bits";
import { Filter, Crosshair, MapPin } from "lucide-react";

// Custom colored pin factory using inline SVG data URL
const makeIcon = (color) =>
  L.divIcon({
    className: "cw-pin",
    html: `<div style="
      width:32px;height:32px;transform:translate(-50%,-100%);
      display:flex;align-items:center;justify-content:center;">
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32' height='32'>
        <path d='M12 2C7.6 2 4 5.6 4 10c0 5.5 8 12 8 12s8-6.5 8-12c0-4.4-3.6-8-8-8z'
              fill='${color}' stroke='#0D0E10' stroke-width='1.5'/>
        <circle cx='12' cy='10' r='3' fill='#0D0E10'/>
      </svg></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

const STATUS_COLORS = {
  open: "#10B981",
  negotiating: "#F59E0B",
  accepted: "#3B82F6",
  in_progress: "#3B82F6",
  completed: "#059669",
  cancelled: "#EF4444",
};

const STATUS_LABELS = {
  open: "Aberta",
  negotiating: "Negociando",
  accepted: "Aceita",
  in_progress: "Em andamento",
  completed: "Concluída",
  cancelled: "Cancelada",
};

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

export default function MapView() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [radiusKm, setRadiusKm] = useState(10);
  const [statusFilter, setStatusFilter] = useState(new Set(["open", "negotiating"]));
  const [center, setCenter] = useState([user?.latitude || -23.5505, user?.longitude || -46.6333]);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/shifts", {
          params: { lat: center[0], lng: center[1] },
        });
        setShifts(data.shifts);
      } finally { setLoading(false); }
    })();
  }, [center]);

  const visibleShifts = useMemo(
    () => shifts.filter(s =>
      statusFilter.has(s.status) &&
      s.latitude && s.longitude &&
      (s.distance_km == null || s.distance_km <= radiusKm)
    ),
    [shifts, statusFilter, radiusKm]
  );

  const toggleStatus = (st) => {
    setStatusFilter(prev => {
      const n = new Set(prev);
      n.has(st) ? n.delete(st) : n.add(st);
      return n;
    });
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setCenter([pos.coords.latitude, pos.coords.longitude]); setLocating(false); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-4 anim-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-extrabold">Mapa de Diárias</h1>
          <p className="text-zinc-400 text-sm mt-1">Explore as vagas por região com raio ajustável.</p>
        </div>
        <button onClick={locateMe} className="cw-btn-outline text-sm inline-flex items-center gap-2" data-testid="btn-locate-me">
          <Crosshair className="w-4 h-4" /> {locating ? "Localizando…" : "Minha localização"}
        </button>
      </div>

      <div className="cw-card p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          {Object.entries(STATUS_LABELS).map(([k, label]) => (
            <button
              key={k}
              onClick={() => toggleStatus(k)}
              data-testid={`map-filter-${k}`}
              className={`badge-pill border transition-colors ${
                statusFilter.has(k)
                  ? "text-white border-transparent"
                  : "bg-transparent border-zinc-700 text-zinc-500"
              }`}
              style={statusFilter.has(k) ? { background: STATUS_COLORS[k] + "40", borderColor: STATUS_COLORS[k], color: STATUS_COLORS[k] } : {}}
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_COLORS[k] }} />
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
          <span className="text-xs uppercase text-zinc-500 tracking-wider">Raio</span>
          <input
            type="range" min="1" max="50" step="1"
            value={radiusKm}
            onChange={e => setRadiusKm(parseInt(e.target.value))}
            className="flex-1 accent-red-600"
            data-testid="map-radius-slider"
          />
          <span className="text-sm font-mono text-red-500 font-bold w-16 text-right">{radiusKm} km</span>
        </div>
        <div className="text-xs text-zinc-400">
          <MapPin className="w-3 h-3 inline mr-1 text-red-500" />
          {visibleShifts.length} vaga(s) no raio
        </div>
      </div>

      <div className="cw-card overflow-hidden" style={{ height: "560px" }}>
        <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Recenter center={center} />
          <Circle center={center} radius={radiusKm * 1000}
            pathOptions={{ color: "#DC2626", fillColor: "#DC2626", fillOpacity: 0.05, weight: 1.5, dashArray: "6" }} />
          <Marker position={center} icon={makeIcon("#DC2626")}>
            <Popup>Você está aqui</Popup>
          </Marker>
          {visibleShifts.map(s => (
            <Marker key={s.shift_id} position={[s.latitude, s.longitude]} icon={makeIcon(STATUS_COLORS[s.status] || "#71717A")}>
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>{s.commerce_name}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{s.date} · {s.start_time}–{s.end_time}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>{s.address}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#DC2626", marginTop: 6 }}>{currency(s.final_rate)}</div>
                  <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginTop: 6, background: STATUS_COLORS[s.status] + "30", color: STATUS_COLORS[s.status] }}>
                    {STATUS_LABELS[s.status]}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Link to={`/shifts/${s.shift_id}`} style={{ color: "#DC2626", fontWeight: 600, fontSize: 13 }}>
                      Ver detalhes →
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Carregando vagas…</div>}
    </div>
  );
}
